import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  TablePagination,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import {
  TransferWithinAStation as TransferIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';


// Mock data for transfer history
const mockTransferHistory = [
  {
    id: 1,
    date: '2024-01-15',
    time: '10:30 AM',
    fromAssociate: 'John Smith',
    fromAssociateId: 'AS001',
    toAssociate: 'Sarah Johnson',
    toAssociateId: 'AS002',
    leadId: 'LD001',
    leadName: 'ABC Company Lead',
    status: 'completed',
    reason: 'Better expertise match',
    initiatedBy: 'Admin User',
  },
  {
    id: 2,
    date: '2024-01-14',
    time: '02:15 PM',
    fromAssociate: 'Mike Wilson',
    fromAssociateId: 'AS003',
    toAssociate: 'Emily Davis',
    toAssociateId: 'AS004',
    leadId: 'LD002',
    leadName: 'XYZ Corporation Lead',
    status: 'pending',
    reason: 'Workload redistribution',
    initiatedBy: 'Manager User',
  },
  {
    id: 3,
    date: '2024-01-13',
    time: '09:45 AM',
    fromAssociate: 'David Brown',
    fromAssociateId: 'AS005',
    toAssociate: 'Lisa Anderson',
    toAssociateId: 'AS006',
    leadId: 'LD003',
    leadName: 'Tech Startup Lead',
    status: 'completed',
    reason: 'Geographic preference',
    initiatedBy: 'Admin User',
  },
  {
    id: 4,
    date: '2024-01-12',
    time: '04:20 PM',
    fromAssociate: 'Robert Taylor',
    fromAssociateId: 'AS007',
    toAssociate: 'Jennifer White',
    toAssociateId: 'AS008',
    leadId: 'LD004',
    leadName: 'Manufacturing Lead',
    status: 'cancelled',
    reason: 'Client preference change',
    initiatedBy: 'Supervisor User',
  },
];

// Mock associates data
const mockAssociates = [
  { id: 'AS001', name: 'John Smith', department: 'Sales' },
  { id: 'AS002', name: 'Sarah Johnson', department: 'Sales' },
  { id: 'AS003', name: 'Mike Wilson', department: 'Sales' },
  { id: 'AS004', name: 'Emily Davis', department: 'Sales' },
  { id: 'AS005', name: 'David Brown', department: 'Sales' },
  { id: 'AS006', name: 'Lisa Anderson', department: 'Sales' },
  { id: 'AS007', name: 'Robert Taylor', department: 'Sales' },
  { id: 'AS008', name: 'Jennifer White', department: 'Sales' },
];

// Mock leads data
const mockLeads = [
  { id: 'LD001', name: 'ABC Company Lead', status: 'Active' },
  { id: 'LD002', name: 'XYZ Corporation Lead', status: 'Active' },
  { id: 'LD003', name: 'Tech Startup Lead', status: 'Active' },
  { id: 'LD004', name: 'Manufacturing Lead', status: 'Active' },
  { id: 'LD005', name: 'Retail Chain Lead', status: 'Active' },
];

const ChangeAssociate = () => {
  const history = useHistory();
  const [transferHistory, setTransferHistory] = useState(mockTransferHistory);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleTransferProcess = () => {
    history.push('/app/lead/TransferProcess');
  };

  const handleViewTransfer = (transferId) => {
    const transfer = transferHistory.find(t => t.id === transferId);
    if (transfer) {
      setSelectedTransfer(transfer);
      setViewDialogOpen(true);
    }
  };

  const handleDeleteTransfer = (transferId) => {
    if (window.confirm('Are you sure you want to delete this transfer?')) {
      setTransferHistory(prev => prev.filter(transfer => transfer.id !== transferId));
      setSnackbar({
        open: true,
        message: 'Transfer deleted successfully',
        severity: 'success'
      });
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedTransfer(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredHistory = transferHistory.filter(transfer => {
    const matchesSearch = 
      transfer.fromAssociate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transfer.toAssociate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transfer.leadName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transfer.leadId.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || transfer.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });


  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1, color: '#536DFE', fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#333' }}>
              Associate Transfer Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<TransferIcon />}
            onClick={handleTransferProcess}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Transfer Process
          </Button>
        </Box>
      </Box>

      {/* Search and Filter Controls */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by associate name, lead name, or lead ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#536DFE' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: '#536DFE',
                  color: '#536DFE',
                  '&:hover': {
                    borderColor: '#3F51B5',
                    backgroundColor: 'rgba(83, 109, 254, 0.04)'
                  }
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transfer History Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F6F7FF' }}>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Lead Details</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>From Associate</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>To Associate</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Initiated By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHistory
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((transfer) => (
                <TableRow key={transfer.id} hover sx={{ '&:hover': { backgroundColor: '#F6F7FF' } }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {transfer.date}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {transfer.time}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 1, width: 32, height: 32, backgroundColor: '#536DFE' }}>
                        <BusinessIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {transfer.leadName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          ID: {transfer.leadId}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 1, width: 32, height: 32, backgroundColor: '#FF5C93' }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {transfer.fromAssociate}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {transfer.fromAssociateId}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 1, width: 32, height: 32, backgroundColor: '#3CD4A0' }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {transfer.toAssociate}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {transfer.toAssociateId}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, color: '#555' }}>
                      {transfer.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transfer.status.charAt(0).toUpperCase() + transfer.status.slice(1)}
                      color={getStatusColor(transfer.status)}
                      size="small"
                      sx={{ 
                        minWidth: 100,
                        fontWeight: 500,
                        '& .MuiChip-label': { fontSize: '12px' }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {transfer.initiatedBy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewTransfer(transfer.id)}
                        title="View Details"
                        sx={{ color: '#1976d2' }}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteTransfer(transfer.id)}
                        title="Delete Transfer"
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          
          component="div"
          count={filteredHistory.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {filteredHistory.length === 0 && (
        <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
          <CardContent>
            <Box textAlign="center" py={4}>
              <HistoryIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                No transfer records found
              </Typography>
              <Typography variant="body2" sx={{ color: '#999' }}>
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'No transfers have been recorded yet'
                }
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* View Transfer Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            Transfer Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedTransfer && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  Transfer ID
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedTransfer.id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  Status
                </Typography>
                <Chip
                  label={selectedTransfer.status.charAt(0).toUpperCase() + selectedTransfer.status.slice(1)}
                  color={getStatusColor(selectedTransfer.status)}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  Date & Time
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedTransfer.date} at {selectedTransfer.time}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  Lead Information
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedTransfer.leadName} ({selectedTransfer.leadId})
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  From Associate
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedTransfer.fromAssociate} ({selectedTransfer.fromAssociateId})
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  To Associate
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedTransfer.toAssociate} ({selectedTransfer.toAssociateId})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  Transfer Reason
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedTransfer.reason}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  Initiated By
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedTransfer.initiatedBy}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default ChangeAssociate;
